import { BranchOverviewNavigationParams } from '@app/features/BranchOverview/BranchOverview';
import { CashBalanceNavigationParams } from '@app/features/CashBalance/CashBalanceHelper';
import { WeeklyMonthlyBalanceHistoryParams } from '@app/features/WeeklyMonthlyBalanceHistory/WeeklyMonthlyHistory';
import { MonthlyBalanceNavigationParams } from '@app/monthly-balance/page';
import { PaidInNavigationParams } from '@app/paid-in/page';
import { PaidOutNavigationParams } from '@app/paid-out/page';
import { TransferInNavigationParams } from '@app/transfer-in/page';
import { TransferOutNavigationParams } from '@app/transfer-out/page';

import { AccountingLocationID } from '@api/generator';

export enum SCREENS {
  TransferOut = '/transfer-out',
  TransferIn = '/transfer-in',
  PaidIn = '/paid-in',
  PaidOut = '/paid-out',
  BranchSelection = '/branch-selection',
  TaskManagement = '/task-management',
  TaskManagementSpike = '/task-management-spike',
  RecordKnownVariance = '/record-known-variance',
  DiscrepancyAccount = '/discrepancy-account',
  ViewDiscrepancy = '/view-discrepancy',
  PouchPreparation = '/pouch-preparation',
  PouchPreparationDetails = '/pouch-preparation-details',
  PouchPreparationHistory = '/pouch-preparation-history',
  WeeklyBalance = '/weekly-balance',
  MonthlyBalance = '/monthly-balance',
  ViewMonthlyWeeklyBalance = '/view-monthly-weekly-balance',
  ManageCashDrawer = '/manage-cash-drawers',
  WeeklyMonthlyBalanceHistory = '/weekly-monthly-balance-history',
  TransactionLog = '/transaction-log',
  TransactionViewer = '/transaction-viewer',
  SalesSummary = '/sales-summary',
  BranchOverview = '/branch-overview',
  CashCount = '/cash-balance',
  CashCountHistory = '/cash-count-history',
  CashMovementHistory = '/cash-movement-history',
  StockCheck = '/stock-check',
  ManualBookInStock = '/manual-stock-book',
  BranchOverviewStock = '/branch-overview-stock',
  BranchOverviewForex = '/branch-overview-forex',
  VersionInfo = '/version-info',
  CTAcceptance = 'po://ct/pouch-acceptance',
  CTDespatch = 'po://ct/pouch-despatch',
  FeatureFlagConfig = '/feature-flag-config',
  EnvironmentVariableConfig = '/environment-variable-config',
}

export type ScreenKey = keyof typeof SCREENS;

export const pageTitles: { [key: string]: string } = {
  [SCREENS.BranchSelection]: 'Branch Selection',
  [SCREENS.TaskManagement]: 'Task List',
  [SCREENS.TaskManagementSpike]: 'Task List Spike',
  [SCREENS.RecordKnownVariance]: 'Record a Known Variance',
  [SCREENS.DiscrepancyAccount]: 'Branch Discrepancy Account',
  [SCREENS.PouchPreparation]: 'Pouch Preparation',
  [SCREENS.PouchPreparationDetails]: 'Pouch Preparation',
  [SCREENS.PouchPreparationHistory]: 'Pouch History',
  [SCREENS.WeeklyBalance]: 'Weekly Balance',
  [SCREENS.MonthlyBalance]: 'Monthly Balance',
  [SCREENS.ManageCashDrawer]: 'Manage Cash Drawer',
  [SCREENS.WeeklyMonthlyBalanceHistory]: 'Weekly/Monthly Balance History',
  [SCREENS.TransactionLog]: 'Transaction Log',
  [SCREENS.TransactionViewer]: 'Transaction Viewer',
  [SCREENS.SalesSummary]: 'Sales Summary',
  [SCREENS.BranchOverview]: 'Cash Management Overview',
  [SCREENS.CashCountHistory]: 'Cash Count History',
  [SCREENS.CashMovementHistory]: 'Cash Movement History',
  [SCREENS.StockCheck]: 'Stock Check',
  [SCREENS.ManualBookInStock]: 'Book in Stock',
  [SCREENS.BranchOverviewStock]: 'Stock Management Overview',
  [SCREENS.BranchOverviewForex]: 'Forex Management Overview',
  [SCREENS.VersionInfo]: 'Version Info',
  [SCREENS.PaidIn]: 'Paid In',
  [SCREENS.PaidOut]: 'Paid Out',
  [SCREENS.TransferIn]: 'Cash Transfer In',
  [SCREENS.TransferOut]: 'Cash Transfer Out',
  [SCREENS.CashCount]: 'Cash Balance',
  [SCREENS.FeatureFlagConfig]: 'Feature Flag Config',
  [SCREENS.EnvironmentVariableConfig]: 'Environment Variable Config',
  [SCREENS.ViewDiscrepancy]: 'Branch Discrepancy Account',
};

export const primaryPages = [
  SCREENS.TaskManagement,
  SCREENS.PouchPreparation,
  SCREENS.RecordKnownVariance,
  SCREENS.WeeklyBalance,
  SCREENS.MonthlyBalance,
  SCREENS.DiscrepancyAccount,
  SCREENS.WeeklyMonthlyBalanceHistory,
  SCREENS.PouchPreparationHistory,
  SCREENS.SalesSummary,
  SCREENS.TransactionLog,
  SCREENS.TransactionViewer,
  SCREENS.ManageCashDrawer,
  SCREENS.BranchOverview,
  SCREENS.CashMovementHistory,
  SCREENS.CashCountHistory,
  SCREENS.BranchOverviewStock,
  SCREENS.StockCheck,
  SCREENS.BranchOverviewForex,
];

export const CT_URL_LINK = 'po://ct';

export type BBONavParams = {
  notNavigatedFromMenu?: string;
};

export type NavigatedFromCtParams = {
  fadcode?: string;
  targetRoute?: keyof typeof SCREENS;
  navigatedFromCT?: string;
};

export type ScreenKeysNavParams = {
  TransferIn: NavigatedFromCtParams & BBONavParams & TransferInNavigationParams;
  TransferOut: NavigatedFromCtParams & BBONavParams & TransferOutNavigationParams;
  MonthlyBalance: NavigatedFromCtParams & BBONavParams & MonthlyBalanceNavigationParams;
  WeeklyBalance: NavigatedFromCtParams & BBONavParams & MonthlyBalanceNavigationParams;
  ViewMonthlyWeeklyBalance: NavigatedFromCtParams & BBONavParams & MonthlyBalanceNavigationParams;
  PaidIn: NavigatedFromCtParams & BBONavParams & PaidInNavigationParams;
  PaidOut: NavigatedFromCtParams & BBONavParams & PaidOutNavigationParams;
  CashCount: NavigatedFromCtParams & BBONavParams & CashBalanceNavigationParams;
  BranchOverview: NavigatedFromCtParams & BBONavParams & BranchOverviewNavigationParams;
  WeeklyMonthlyBalanceHistory: NavigatedFromCtParams &
    BBONavParams &
    WeeklyMonthlyBalanceHistoryParams;
  // TODO: Replace below with navigation params from the pages when they are implemented
  ViewDiscrepancy: {
    discrepancyId: string;
  };
  PouchPreparation: {
    pouchId: string;
    notNavigatedFromMenu?: string;
  };
  PouchPreparationDetails: {
    pouchId: string;
    currentAccountingLocationID?: AccountingLocationID;
  };
};

export const constructRouteWithParams = <SK extends ScreenKey>(
  routeKey: SK,
  queryParams?: SK extends keyof ScreenKeysNavParams ? ScreenKeysNavParams[SK] : undefined,
) => {
  const route: string = SCREENS[routeKey];
  let routeWithParams: string = route;

  if (queryParams !== undefined) {
    const searchParams = new URLSearchParams(queryParams).toString();
    routeWithParams += `?${searchParams}`;
  }
  return routeWithParams;
};
